import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling
 * scrolling when the slideshow is in the viewport.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance.
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }

    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed.
   * @param {Slideshow} slideshow
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (
              entry.target
            );

            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow.
   * @param {Slideshow} slideshow
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#scroll || !this.refs.slides) {
          return;
        }

        const index = parseInt(newValue, 10) || 0;
        const slideId = this.refs.slides[index]?.getAttribute('slide-id');

        if (slideId) {
          this.select(
            { id: slideId },
            undefined,
            { animate: false }
          );
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    SlideshowViewportObserver.getInstance().observe(this);

    if (viewTransition.current) {
      await viewTransition.current;

      if (!this.isConnected) {
        return;
      }
    }

    const slideCount = this.slides?.length || 0;

    if (slideCount <= 1) {
      this.#setupSlideshowWithoutControls();
    } else {
      this.#setupSlideshow();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;

      scroller.removeEventListener(
        'mousedown',
        this.#handleMouseDown
      );

      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;

    if (slideCount > 1) {
      this.removeEventListener(
        'mouseenter',
        this.suspend
      );

      this.removeEventListener(
        'mouseleave',
        this.resume
      );

      this.removeEventListener(
        'pointerenter',
        this.#handlePointerEnter
      );

      document.removeEventListener(
        'visibilitychange',
        this.#handleVisibilityChange
      );
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Whether this slideshow is nested inside another slideshow.
   */
  get isNested() {
    return (
      this.parentElement?.closest('slideshow-component') !== null
    );
  }

  /**
   * The initial slide element.
   */
  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide.
   *
   * @param {number|string|{id: string}} input
   * @param {Event} [event]
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  async select(input, event, options = {}) {
    if (
      this.#disabled ||
      !this.refs.slides?.length ||
      !this.#scroll
    ) {
      return;
    }

    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    let requestedIndex = (() => {
      if (typeof input === 'number') {
        return input;
      }

      if (typeof input === 'string') {
        return parseInt(input, 10);
      }

      if ('id' in input) {
        const requestedSlide = this.refs.slides.find(
          (slide) => slide.getAttribute('slide-id') == input.id
        );

        if (!requestedSlide || !this.slides) {
          return;
        }

        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    if (
      !slides?.length ||
      requestedIndex === undefined ||
      Number.isNaN(requestedIndex)
    ) {
      return;
    }

    if (!this.infinite) {
      requestedIndex = clamp(
        requestedIndex,
        0,
        slides.length - 1
      );
    }

    const requestedSlideElement = slides[requestedIndex];

    /*
     * 如果点击的是当前图片，只需要恢复它的显示状态。
     * 这也能防止手机端首图已经被选中时，因为提前 return
     * 而一直停留在 aria-hidden="true"。
     */
    if (currentSlide === requestedSlideElement) {
      requestedSlideElement?.setAttribute(
        'aria-hidden',
        'false'
      );

      if (requestedSlideElement) {
        this.#visibleSlides = [requestedSlideElement];
      }

      return;
    }

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    let index = requestedIndex;

    if (requestedIndex < 0) {
      index = lastIndex;
    } else if (requestedIndex > lastIndex) {
      index = 0;
    }

    const slide = slides[index];

    if (!slide) {
      return;
    }

    const previousIndex = this.current;

    /*
     * 相邻图片可以正常滚动。
     * 点击距离较远的缩略图时直接定位，防止动画期间锁死。
     */
    const isAdjacentSlide =
      Math.abs(index - current) <= 1 &&
      requestedIndex >= 0 &&
      requestedIndex <= lastIndex;

    const instant =
      prefersReducedMotion() ||
      !animate ||
      !isAdjacentSlide;

    slide.setAttribute('aria-hidden', 'false');

    this.#visibleSlides = [slide];

    this.#scroll.to(slide, {
      instant,
    });

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(
      index,
      instant ? 'instant' : 'smooth'
    );

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   *
   * @param {Event} [event]
   * @param {Object} [options]
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   *
   * @param {Event} [event]
   * @param {Object} [options]
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic playback.
   *
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) {
      return;
    }

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) {
        return;
      }

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic playback.
   */
  resume() {
    if (!this.autoplay || this.paused) {
      return;
    }

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) {
      return undefined;
    }

    return value * 1000;
  }

  /**
   * Current slide index.
   *
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Updates the current slide and controls.
   *
   * @type {number}
   */
  set current(value) {
    const {
      current,
      thumbnails,
      dots,
      slides,
      previous,
      next,
    } = this.refs;

    this.#current = value;

    if (current) {
      current.textContent = `${value + 1}`;
    }

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((element, index) => {
        element.setAttribute(
          'aria-selected',
          `${index === value}`
        );
      });
    }

    if (previous) {
      previous.disabled = Boolean(
        !this.infinite && value === 0
      );
    }

    if (next) {
      next.disabled = Boolean(
        !this.infinite &&
          slides &&
          this.nextIndex >= slides.length
      );
    }
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  get previousIndex() {
    const { current, visibleSlides } = this;

    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;

    const modifier =
      visibleSlides.length > 1
        ? visibleSlides.length
        : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length
      ? current === 0
      : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length
      ? current === slides.length - 1
      : false;
  }

  /**
   * Sets the disabled attribute.
   *
   * @param {boolean} value
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }

  /**
   * Whether the slideshow is disabled.
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' ||
      (
        this.hasAttribute('mobile-disabled') &&
        !mediaQueryLarge.matches
      )
    );
  }

  /**
   * Whether the slideshow is temporarily disabled.
   *
   * @type {boolean}
   */
  #disabled = false;

  /**
   * Automatic playback interval.
   *
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Scroller instance.
   *
   * @type {Scroller}
   */
  #scroll;

  /**
   * ResizeObserver instance.
   *
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver instance.
   *
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Currently visible slides.
   *
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Sets up a slideshow with zero or one slide.
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;

    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;

      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute(
        'aria-hidden',
        'false'
      );

      this.#visibleSlides = [this.refs.slides[0]];
    }
  }

  /**
   * Sets up a slideshow with multiple slides.
   */
  #setupSlideshow() {
    this.#setupIntersectionObserver();

    const { scroller } = this.refs;

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener(
      'mousedown',
      this.#handleMouseDown
    );

    this.addEventListener(
      'mouseenter',
      this.suspend
    );

    this.addEventListener(
      'mouseleave',
      this.resume
    );

    this.addEventListener(
      'pointerenter',
      this.#handlePointerEnter
    );

    document.addEventListener(
      'visibilitychange',
      this.#handleVisibilityChange
    );

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    /*
     * 手机端修复：
     * IntersectionObserver 的第一次回调是异步执行的。
     * 在它返回结果之前，先强制显示初始图片，避免首图被
     * 设置为 aria-hidden="true"。
     */
    const initialSlide = this.initialSlide;

    if (initialSlide) {
      this.#visibleSlides = [initialSlide];

      initialSlide.setAttribute(
        'aria-hidden',
        'false'
      );
    }

    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;

      const initialSlideId =
        this.initialSlide?.getAttribute('slide-id');

      requestAnimationFrame(() => {
        if (
          this.initialSlideIndex !== 0 &&
          initialSlideId
        ) {
          this.select(
            { id: initialSlideId },
            undefined,
            { animate: false }
          );

          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount =
            this.#updateVisibleSlides();

          /*
           * 如果浏览器暂时没有检测到可见图片，
           * 强制恢复第一张或初始图片。
           */
          if (visibleSlidesAmount === 0) {
            const fallbackSlide =
              this.initialSlide ||
              this.slides?.[0];

            if (fallbackSlide) {
              this.#visibleSlides = [fallbackSlide];

              fallbackSlide.setAttribute(
                'aria-hidden',
                'false'
              );

              this.#scroll.to(fallbackSlide, {
                instant: true,
              });

              const fallbackIndex =
                this.slides?.indexOf(fallbackSlide) ?? 0;

              this.current = Math.max(
                0,
                fallbackIndex
              );
            }

            visibleSlidesAmount = 1;
          }
        }
      });

      this.#resizeObserver = new ResizeObserver(
        async () => {
          if (viewTransition.current) {
            await viewTransition.current;
          }

          if (visibleSlidesAmount > 1) {
            this.#updateVisibleSlides();
          }

          if (
            this.hasAttribute('auto-hide-controls')
          ) {
            this.#updateControlsVisibility();
          }
        }
      );

      this.#resizeObserver.observe(
        this.refs.slideshowContainer
      );
    });
  }

  /**
   * Handles user scrolling.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) {
      return;
    }

    const slide = this.slides?.[index];

    if (!slide) {
      return;
    }

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the current slide.
   *
   * @returns {number}
   */
  #sync = () => {
    const { slides } = this;

    if (!slides || !this.#scroll) {
      return (this.current = 0);
    }

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) {
      return this.current;
    }

    const { axis } = this.#scroll;
    const { scroller } = this.refs;

    const centers = visibleSlides.map(
      (slide) => center(slide, axis)
    );

    const referencePoint =
      visibleSlides.length > 1
        ? scroller.getBoundingClientRect()[axis]
        : center(scroller, axis);

    const closestCenter = closest(
      centers,
      referencePoint
    );

    const closestVisibleSlide =
      visibleSlides[
        centers.indexOf(closestCenter)
      ];

    if (!closestVisibleSlide) {
      return (this.current = 0);
    }

    const index = slides.indexOf(
      closestVisibleSlide
    );

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Starts drag handling.
   *
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    if (this.disabled || this.#dragging) {
      return;
    }

    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();

    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;

    const startTime = performance.now();

    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * @param {PointerEvent} pointerEvent
     */
    const onPointerMove = (pointerEvent) => {
      const current = pointerEvent[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) {
        return;
      }

      if (!moved) {
        moved = true;

        this.setPointerCapture(
          pointerEvent.pointerId
        );

        document.addEventListener(
          'click',
          preventDefault,
          {
            once: true,
            signal,
          }
        );

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        const closestSlideshow =
          this.parentElement?.closest(
            'slideshow-component'
          );

        const isNested =
          closestSlideshow instanceof Slideshow &&
          closestSlideshow !== this;

        const cannotMoveInDirection =
          (movingRight && this.atStart) ||
          (movingLeft && this.atEnd);

        if (
          isNested &&
          cannotMoveInDirection
        ) {
          controller.abort();
          this.#dragging = false;
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      pointerEvent.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta =
        performance.now() - startTime;

      velocity = Math.round(
        (delta / timeDelta) * 1000
      );

      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, {
        instant: true,
      });
    };

    /**
     * @param {PointerEvent} pointerEvent
     */
    const onPointerUp = async (
      pointerEvent
    ) => {
      controller.abort();

      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) {
        return;
      }

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier =
        current !== next ||
        Math.abs(velocity) < 10 ||
        distanceTravelled < 10
          ? 0
          : direction;

      const newIndex = clamp(
        next + modifier,
        0,
        slides.length - 1
      );

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) {
        throw new Error(
          `Slide not found at index ${newIndex}`
        );
      }

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');

      if (
        this.hasPointerCapture(
          pointerEvent.pointerId
        )
      ) {
        this.releasePointerCapture(
          pointerEvent.pointerId
        );
      }

      this.#centerSelectedThumbnail(
        newIndex
      );

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      if (this.#dragging) {
        return;
      }

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener(
      'pointermove',
      onPointerMove,
      { signal }
    );

    document.addEventListener(
      'pointerup',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointerdown',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercancel',
      onPointerUp,
      { signal }
    );

    document.addEventListener(
      'pointercapturelost',
      onPointerUp,
      { signal }
    );
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  /**
   * Returns slides that are not hidden.
   */
  get slides() {
    return this.refs.slides?.filter(
      (slide) =>
        !slide.hasAttribute('hidden') ||
        slide.hasAttribute('reveal')
    );
  }

  /**
   * Initial slide index.
   */
  get initialSlideIndex() {
    const initialSlide =
      this.getAttribute('initial-slide');

    if (initialSlide == null) {
      return 0;
    }

    return parseInt(initialSlide, 10);
  }

  /**
   * Pauses when the browser tab is hidden.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  };

  /**
   * Updates automatic control visibility.
   */
  #updateControlsVisibility() {
    if (
      !this.hasAttribute('auto-hide-controls')
    ) {
      return;
    }

    const {
      scroller,
      slideshowControls,
    } = this.refs;

    if (
      !(slideshowControls instanceof HTMLElement)
    ) {
      return;
    }

    slideshowControls.hidden =
      scroller.scrollWidth <=
      scroller.offsetWidth;
  }

  /**
   * Tracks visible slides.
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;

    if (!slides?.length) {
      return;
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver =
      new IntersectionObserver(
        (entries) => {
          const allEntries = [
            ...entries,
            ...(
              this.#intersectionObserver
                ? this.#intersectionObserver.takeRecords()
                : []
            ),
          ];

          for (const entry of allEntries) {
            const slide =
              /** @type {HTMLElement} */ (
                entry.target
              );

            const isCurrentlyVisible =
              this.#visibleSlides.includes(slide);

            const shouldBeVisible =
              entry.intersectionRatio >=
              SLIDE_VISIBLITY_THRESHOLD;

            if (
              shouldBeVisible &&
              !isCurrentlyVisible
            ) {
              this.#visibleSlides.push(slide);
            } else if (
              !shouldBeVisible &&
              isCurrentlyVisible
            ) {
              const index =
                this.#visibleSlides.indexOf(
                  slide
                );

              if (index > -1) {
                this.#visibleSlides.splice(
                  index,
                  1
                );
              }
            }
          }

          /*
           * 某些手机浏览器在初始化期间会短暂返回空数组。
           * 如果当前没有可见图片，则继续保留当前图片或第一张图片。
           */
          if (
            this.#visibleSlides.length === 0
          ) {
            const fallbackSlide =
              this.slides?.[this.current] ||
              this.initialSlide ||
              this.slides?.[0];

            if (fallbackSlide) {
              this.#visibleSlides = [
                fallbackSlide,
              ];

              fallbackSlide.setAttribute(
                'aria-hidden',
                'false'
              );
            }
          }

          this.#visibleSlides.sort(
            (firstSlide, secondSlide) =>
              slides.indexOf(firstSlide) -
              slides.indexOf(secondSlide)
          );

          this.#updateVisibleSlides();
        },
        {
          root: scroller,
          threshold:
            SLIDE_VISIBLITY_THRESHOLD,
          rootMargin: '1px',
        }
      );

    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(
        slide
      );
    });
  }

  /**
   * Centers the selected thumbnail.
   *
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #centerSelectedThumbnail(
    index,
    behavior = 'smooth'
  ) {
    const selectedThumbnail =
      this.refs.thumbnails?.[index];

    if (!selectedThumbnail) {
      return;
    }

    const { thumbnailsContainer } =
      this.refs;

    if (
      !thumbnailsContainer ||
      !(
        thumbnailsContainer instanceof
        HTMLElement
      )
    ) {
      return;
    }

    const { slideshowControls } =
      this.refs;

    if (
      !slideshowControls ||
      !(
        slideshowControls instanceof
        HTMLElement
      )
    ) {
      return;
    }

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Updates aria-hidden for each slide.
   *
   * @returns {number}
   */
  #updateVisibleSlides() {
    const { slides } = this;

    if (!slides?.length) {
      return 0;
    }

    /*
     * 防止移动端初始化期间没有任何可见图片。
     */
    if (this.#visibleSlides.length === 0) {
      const fallbackSlide =
        slides[this.current] ||
        this.initialSlide ||
        slides[0];

      if (fallbackSlide) {
        this.#visibleSlides = [
          fallbackSlide,
        ];
      }
    }

    const visibleSlides = [
      ...this.#visibleSlides,
    ];

    scheduler.schedule(() => {
      slides.forEach((slide) => {
        const isVisible =
          visibleSlides.includes(slide);

        slide.setAttribute(
          'aria-hidden',
          `${!isVisible}`
        );
      });
    });

    return visibleSlides.length;
  }
}

if (
  !customElements.get('slideshow-component')
) {
  customElements.define(
    'slideshow-component',
    Slideshow
  );
}